import { OnlyOfficeEditor } from "@/components/common/OnlyOfficeEditor.tsx";
import ConfirmWithPasswordModal from "@/components/confirm-with-password-modal";
import Alert from "@/components/core/alert";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import { Checkbox } from "@/components/core/forms/checkbox";
import { CheckboxGroup } from "@/components/core/forms/checkbox-group";
import Combobox from "@/components/core/forms/combobox";
import ComboboxCreatable from "@/components/core/forms/combox-creatable";
import FormCalendar from "@/components/core/forms/form-calendar";
import { Form, FormControl, FormDescription, FormField, FormGroup, FormItem, FormLabel, FormMessage } from "@/components/core/forms/form.tsx";
import { Input } from "@/components/core/forms/input.tsx";
import Select from "@/components/core/forms/select";
import TagsInput from "@/components/core/forms/tags-input";
import { Textarea } from "@/components/core/forms/textarea";
import { useModal } from "@/components/core/modals/use-modal";
import { useModalControl } from "@/components/core/modals/use-modal-controller";
import { ScrollArea } from "@/components/core/scroller";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs";
import Tooltip from "@/components/core/tooltip";
import { PdfViewer } from "@/components/pdf-viewer";
import { caseStages } from "@/constants/case-stages";
import { caseUserRoles } from "@/constants/case-user-roles";
import { userRoles } from "@/constants/user-roles";
import { caseRespondentsQueryOptions, caseStagesQueryOptions } from "@/features/case-record/api/queries/case-stage-query";
import type { CaseRecord } from "@/features/case-record/types/case-records.ts";
import { useDocumentConfigMutation } from "@/features/dashboard/api/queries/document-config-queries.ts";
import eventService from "@/features/dashboard/api/services/event-service";
import { useAuth } from "@/hooks/use-auth";
import { queryClient } from "@/query-client";
import { toOrdinal } from "@/utils/format-data";
import { formatDate } from "@/utils/format-date";
import { zodResolver } from "@hookform/resolvers/zod";
import { IconAlertCircle, IconCirclePlus, IconEye, IconFile, IconTemplate, IconTrash, IconX } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";
import { debounce, set } from "lodash";
import moment from "moment";
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useCaseActivityEditMutation, useCaseConvertDocsMutation } from "../api/queries/acitivities-query";
import caseEvidenceService from "../api/services/evidence-service";
import formTemplateService from "../api/services/form-templates-service";
import { type CaseActivity, type CaseActivityForm, caseActivityFormSchema } from "../types/activities";

export default function CaseActivityEditModal({
	caseRecord,
	data,
	title = "",
	moveTo = false,
	decision = false,
	allowPostAs = false,
	nextStage = null,
	collaborators = [],
	nextVisibleStage = null,
	isSheriffReport = false,
}: {
	caseRecord: CaseRecord;
	nextStage: object;
	nextVisibleStage: object;
	data: CaseActivity;
	title: string;
	moveTo: boolean;
	allowPostAs: boolean;
	decision: boolean;
	isSheriffReport: boolean;
	collaborators: any[];
}) {
	const isSummon = data?.tag === "Summons";
	const modal = useModal();
	const navigate = useNavigate();
	const { hasRole, user } = useAuth();
	const { closeModal } = useModalControl();
	const documentTokenMutation = useDocumentConfigMutation();
	const caseActivitiesMutation = useCaseActivityEditMutation(data?.id);
	const convertDocsMutation = useCaseConvertDocsMutation();
	const [compliance, setCompliance] = useState(
		isSummon || data?.activity === "Notice of Pre-Trial Conference" || (data?.respondents !== "null" ? true : data?.with_compliance) || false,
	);
	const [deadline, setDeadline] = useState(isSummon || data?.activity === "Notice of Pre-Trial Conference" || data?.due_at);
	const [inCalendar, setInCalendar] = useState(!!data?.scheduled_at);

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [fees, setFees] = useState(false);
	const [types, setTypes] = useState(null);
	const [defaultDefendants, setDefaultDefendants] = useState<any[]>([]);
	const [type, setType] = useState(data?.form_template_id ? "Template" : "Custom");
	const [selectType, setSelectType] = useState(false);
	const [parties, setParties] = useState<string>();

	const respondentsQuery = useSuspenseQuery(caseRespondentsQueryOptions(caseRecord.id));
	const respondents = respondentsQuery.data;
	const getRole = (role: string) => {
		if (role === caseUserRoles.DEFENDANT) {
			return caseRecord?.defendant_label;
		}
		if (role === caseUserRoles.PLAINTIFF) {
			return caseRecord?.plaintiff_label;
		}

		return role;
	};

	const respondentsMap = caseRecord.collaborators
		.filter((role) => role.role === "Defendant")
		.map((item) => ({
			label: `${item?.full_name} - ${item.role}`,
			value: item,
		}));

	const userCounsel = caseRecord.collaborators.find((collaborator) => collaborator.user_id === data.user_id && collaborator.role.includes("Counsel"));

	const form = useForm<CaseActivityForm>({
		resolver: zodResolver(caseActivityFormSchema),
		defaultValues: {
			id: data?.id,
			case_id: data?.case_id || caseRecord.id,
			document_key: "",
			party_type: data.post_as
				? data.post_as
				: data?.party_type === "Plaintiff" || data?.party_type === "Defendant"
					? userCounsel.id
					: hasRole([userRoles.JUDGE])
						? "Judge"
						: "Bcc",
			type: data?.form_template_id ? "Template" : "Custom",
			stage: data?.stage,
			activity_id: data?.activity_id,
			assigned_to: data?.assigned_to || "Both",
			activity: data?.activity,
			summons: isSummon ? "true" : null,
			tag: data.tag,
			format_type: data?.format_type || "upload_files",
			remarks: data?.remarks,
			move_to_next_stage: data?.move_to_next_stage || false,
			attachment: null,
			ordered_at: data?.ordered_at,
			sheriff_report: null,
			defendants: [],
			submitted_at: data?.submitted_at,
			action_type: data?.with_grant ? "with_grant" : "with_compliance",
			scheduled_at: data?.scheduled_at ? new Date(data?.scheduled_at).toISOString().split("T")[0] : null,
			due_at: data?.due_at ? new Date(data?.due_at).toISOString().split("T")[0] : null,
			filed_at: data?.filed_at,
			close_case: false,
			case_document: data?.case_document || undefined,
			in_calendar: data?.in_calendar || false,
			respondents: JSON.parse(data?.respondents),
			form_template_id: data?.form_template_id || null,
			with_decision: data?.decision || data?.activity === "Judgment" || false,
			decision_at: data?.decision_at || null,
			with_fees: data?.with_fees || false,
			with_grant: data?.with_grant || false,
			with_compliance: isSummon || data?.activity === "Notice of Pre-Trial Conference" || data?.respondents ? true : data?.with_compliance || false,
			with_deadline: isSummon || data?.activity === "Notice of Pre-Trial Conference" || data?.due_at ? true : data?.with_deadline || false,
			decision: data?.decision || false,
			hearings: [
				{
					title: data?.activity || "Hearing",
					type: data?.activity || "Hearing",
					assigned_to: "Both",
					remarks: data?.remarks || "",
					due_date: data?.due_at || "",
					exhibits: data?.exhibits || "",
					stage: nextVisibleStage ? nextVisibleStage.name : data?.stage,
				},
			],
		},
	});
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [multipleFiles, setMultipleFiles] = useState<File[]>([]);
	const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
	const [showAttachment, setShowAttachment] = useState(false);

	const { watch } = form;
	const isNextStage = watch("move_to_next_stage");
	const sheriffReport = watch("sheriff_report");
	const withDecision = watch("with_decision");
	const [docOpts, setDocOpts] = useState({
		token: "",
		options: {},
	});
	const formatType = watch("format_type");

	const caseStageQuery = useSuspenseQuery(caseStagesQueryOptions());
	const caseStagesNew = caseStageQuery.data;

	const updateDate = (days: number) => {
		const today = new Date();
		const dateDue = new Date(today.setDate(today.getDate() + days));
		const date = `${dateDue.toISOString().split("T")[0]}`;
		form.setValue("decision_at", date);
	};

	const handleRemoveAttachment = (index: number) => {
		setSelectedFiles((prevFiles) => {
			const updatedFiles = prevFiles.filter((_, i) => i !== index);
			form.setValue("attachment", updatedFiles);
			setMultipleFiles(updatedFiles);
			setShowAttachment(updatedFiles.length > 0);
			return updatedFiles;
		});
	};

	const baseUrl = (import.meta.env.VITE_PUBLIC_API_BASE_URL || "http://localhost:8000").replace(/\/api$/, "");

	const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (files && files.length > 0) {
			const file = files[0];
			const objectUrl = URL.createObjectURL(file);
			if (file && file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
				const responseDocs = await convertDocsMutation.mutateAsync(file);

				const response = await documentTokenMutation.mutateAsync({
					file_type: "docx",
					title: responseDocs?.path,
					url: `${baseUrl}${responseDocs?.url}`,
					mode: "view",
				});

				setDocOpts({
					token: response.token,
					options: response.options,
				});

				setDocument(responseDocs);
			} else {
				setDocument(objectUrl);
			}
			setSelectedFiles((prevFiles) => {
				const newFiles = [...prevFiles, ...Array.from(files)];
				form.setValue("attachment", newFiles);
				setMultipleFiles(newFiles);
				setShowAttachment(true);
				return newFiles;
			});
		}
	};

	const submit = async (values) => {
		setIsSubmitting(true);
		try {
			await caseActivitiesMutation.mutateAsync(values);
			await queryClient.invalidateQueries({ queryKey: ["case_activities"] });
			await queryClient.invalidateQueries({ queryKey: ["case_records"] });
			if (decision) {
				await navigate({
					to: `/case-evaluation/${caseRecord.id}`,
					search: (old) => ({
						...old,
						stage: "POST_JUDGMENT",
					}),
					replace: true,
				});
			} else {
				if (isNextStage && nextStage?.name !== null && nextStage?.is_button !== true) {
					if (nextStage?.is_judgment) {
						await navigate({
							to: `/case-evaluation/${caseRecord.id}`,
							search: (old) => ({
								...old,
								stage: "POST_JUDGMENT",
							}),
							replace: true,
						});
					} else {
						await navigate({
							to: `/case-evaluation/${caseRecord.id}`,
							search: (old) => ({
								...old,
								stage: nextStage?.name,
							}),
							replace: true,
						});
					}
				} else {
					await navigate({
						to: `/case-evaluation/${caseRecord.id}`,
						search: (old) => ({
							...old,
							stage: nextStage?.next_stage,
						}),
						replace: true,
					});
				}
			}

			closeModal();
		} catch (error) {
			setIsSubmitting(false);
		}
	};

	const [isHoliday, setIsHoliday] = useState(false);
	const storedTime = sessionStorage.getItem("password_entered_at");

	const handleDraft = async (values: CaseActivityForm) => {
		const isValid = await form.trigger();
		form.setValue("state", "draft");
		if (isValid) {
			handleSubmit(form.getValues());
		}
	};

	const handleSubmit = async (values: CaseActivityForm) => {
		const defendantsLength = values.defendants?.length || 0;
		const updatedValues = {
			...values,
			length: defendantsLength,
		};

		const expirationTime = moment(storedTime).add(5, "minutes");
		if (storedTime && expirationTime !== undefined && moment().isBefore(expirationTime)) {
			submit(updatedValues);
		} else {
			modal.openModal({
				allowClose: true,
				title: "Confirm Submit",
				description: "Are you sure? This action cannot be undone.",
				size: "sm",
				showHeader: true,
				showFooter: false,
				component: (
					<ConfirmWithPasswordModal
						onSubmit={async () => {
							await submit(updatedValues);
						}}
					/>
				),
			});
		}
	};

	const [document, setDocument] = useState<string | null>(null);

	const hearings = useFieldArray({
		control: form.control,
		name: "hearings",
	});

	const [debouncedDate, setDebouncedDate] = useState("");
	const [rowCounts, setRowCount] = useState(0);

	const handleAddRow = () => {
		hearings.append({
			title: "Hearing",
			type: "Hearing",
			assigned_to: "Both",
			remarks: "",
			due_date: "",
			exhibits: "",
			stage: nextVisibleStage ? nextVisibleStage.name : data?.stage,
		});
	};

	const hearingDate = form.watch("hearings").map((hearing: { due_date: string }) => {
		return hearing.due_date;
	});

	const updateDebouncedDate = useCallback(
		debounce(
			(newDate) => {
				setDebouncedDate(newDate);
			},
			500,
			{ trailing: true },
		),
		[],
	);

	useEffect(() => {
		if (hearingDate) {
			const dates = Array.isArray(hearingDate) ? hearingDate : [hearingDate];

			let hasEmptyDate = false;

			dates.map((data) => {
				if (!data || data.trim() === "") {
					hasEmptyDate = true;
				} else {
					const parsedDate = new Date(data);
					if (!Number.isNaN(parsedDate.getTime())) {
						updateDebouncedDate(format(parsedDate, "yyyy-MM-dd"));
					}
				}
			});
			if (hasEmptyDate) {
				setRowCount(hearings.fields.length - 1);
			} else {
				setRowCount(hearings.fields.length);
			}
		}
	}, [hearingDate, updateDebouncedDate]);

	useEffect(() => {
		if (debouncedDate) {
			eventService
				.validateHolidays(debouncedDate)
				.then((response) => {
					if (response.id) {
						setIsHoliday(true);
					} else {
						setIsHoliday(false);
					}
				})
				.catch((_) => {
					setIsHoliday(false);
				});
		}
	}, [debouncedDate, hearingDate]);

	useEffect(() => {
		if (formatType === "text_editor") {
			documentTokenMutation
				.mutateAsync({
					file_type: "docx",
					title: `New Document for Case No.${caseRecord.case_number}`,
					mode: "edit",
				})
				.then((response) => {
					setDocOpts({
						token: response.token,
						options: response.options,
					});

					form.setValue("document_key", response.key);
				});
		} else {
			// we have to reset the document key since we don't want the backend to assume
			// we're still trying to upload a document from the text-editor
			form.setValue("document_key", null);
		}
	}, [formatType]);

	let selectedCollaborator = [];
	if (!data.post_as && (data?.party_type === "Plaintiff" || data?.party_type === "Defendant") && userCounsel) {
		selectedCollaborator = collaborators
			.filter((collaborator) => collaborator.id === userCounsel.id)
			.map((collaborator) => {
				return {
					label: ` ${caseRecord.case_classification !== "Small Claims" ? "Atty." : ""} ${collaborator?.full_name} - ${getRole(collaborator.role)}`,
					value: collaborator.id,
					role: collaborator.role,
					user_id: collaborator.user_id,
					reference_id: collaborator.reference_id,
				};
			});
	} else {
		selectedCollaborator = collaborators
			.filter((collaborator) => collaborator.id === data.post_as)
			.map((collaborator) => {
				return {
					label: ` ${caseRecord.case_classification !== "Small Claims" ? "Atty." : ""} ${collaborator?.full_name} - ${getRole(collaborator.role)}`,
					value: collaborator.id,
					role: collaborator.role,
					user_id: collaborator.user_id,
					reference_id: collaborator.reference_id,
				};
			});
	}
	useEffect(() => {
		const files = data.attachments;

		if (formatType === "text_editor") {
			documentTokenMutation
				.mutateAsync({
					file_type: "docx",
					title: `New Document for Case No.${caseRecord.case_number}`,
					mode: "edit",
					url: files?.[0]?.original_url,
				})
				.then((response) => {
					setDocOpts({
						token: response.token,
						options: response.options,
					});

					form.setValue("document_key", response.key);
				});
		}

		if (data.attachments) {
			const files = data.attachments;
			if (files && files.length > 0) {
				const objectUrl = files[0].original_url;
				setDocument(objectUrl);
				Promise.all(files.map((file) => urlToFile(file.original_url, file.file_name, "application/pdf"))).then((realFiles) => {
					setSelectedFiles((prevFiles) => {
						const real = [...realFiles];
						form.setValue("attachment", real);
						setMultipleFiles(real);
						setShowAttachment(true);
						return real;
					});
				});
			}
		}

		if (data.hearings) {
			const hearings = data.hearings.map((hearing) => {
				return {
					...hearing,
					id: hearing.id,
					title: hearing.activity,
					due_date: hearing.scheduled_at ? format(new Date(hearing.scheduled_at), "yyyy-MM-dd HH:mm:ss") : "",
				};
			});
			form.setValue("hearings", hearings);
		}

		if (selectedCollaborator.length > 0) {
			form.setValue("party_type", selectedCollaborator[0].value);

			setPartyTypeReference(selectedCollaborator[0].user_id || selectedCollaborator[0].reference_id);
			const postingsFor = collaborators
				.filter((item) => item.id === selectedCollaborator[0].reference_id)
				.map((item) => ({
					label: `${item?.full_name} - ${item.role}`,
					value: item.id,
				}));
			if (postingsFor.length === 1) {
				setDefaultDefendants(postingsFor);
			}
			if (caseRecord.case_classification === "Small Claims") {
				setDefaultDefendants([selectedCollaborator[0]]);
			}
		}
	}, []);

	const [partyTypeReference, setPartyTypeReference] = useState(selectedCollaborator.length > 0 ? selectedCollaborator[0]?.reference_id : "Bcc");
	const [selectedCollaborators, setSelectedCollaborators] = useState(selectedCollaborator.length > 0 ? selectedCollaborator[0] : null);

	async function urlToFile(url, filename, mimeType) {
		const response = await fetch(url);
		const blob = await response.blob();
		return new File([blob], filename, { type: mimeType });
	}

	function formatNames(names: string[]): string {
		if (names.length === 0) return "";
		if (names.length === 1) return names[0];
		if (names.length === 2) return `${names[0]} and ${names[1]}`;
		return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1].replace(" - Defendant", "").replace(" - Plaintiff", "").trim()}`;
	}

	const respondentsInput = form.watch("respondents");
	useEffect(() => {
		if (isSummon) {
			const names = formatNames(respondentsInput.map((respondent: { label: string }) => `${respondent.label}`));
			form.setValue("activity", `Summons - ${names.replace(" - Defendant", "").replace(" - Plaintiff", "").trim()}`);
		}
	}, [respondentsInput]);

	return (
		<Form {...form}>
			<form className="w-full h-full" onSubmit={form.handleSubmit(handleSubmit)}>
				<div className="flex flex-col w-full h-full">
					<div className="flex shrink-0 flex-row items-center justify-between w-full h-[50px] px-4 border-b border-slate-200">
						<span className="text-base font-bold text-foreground-dark">{title?.replace(/_/g, " ")}</span>
						<button
							onClick={() => {
								closeModal();
							}}
							className="flex w-[20px] h-[20px] ring-offset-background transition-opacity outline-none border-none ring-0 text-foreground opacity-40 hover:opacity-100 disabled:pointer-events-none"
							type="button"
						>
							<IconX size={20} stroke={1.5} />
						</button>
					</div>

					<div className="flex flex-col w-full h-full">
						<Tabs className="flex h-full" variant="compact" defaultValue="activity-details">
							<TabsList className="pt-4">
								<TabsTrigger value="activity-details">Details</TabsTrigger>
								<TabsTrigger value="hearing">Hearing</TabsTrigger>
							</TabsList>
							<TabsContent className="h-[calc(100svh-170px)]" value="activity-details">
								<PanelGroup className="flex flex-row grow w-full" direction="horizontal">
									<Panel defaultSize={30} minSize={30} maxSize={30} className="flex flex-col h-full min-w-[420px] max-w-[420px]">
										<div className="flex flex-col justify-between bg-background">
											<ScrollArea className="h-[calc(100svh-170px)]">
												<fieldset disabled={isSubmitting}>
													<div className="grid grid-cols-12 w-full p-4 gap-x-3 gap-y-2">
														{fees && (
															<div className="col-span-12 w-full">
																<Alert type="info" allowClose={false} title="This Court Action has fees" icon={<IconAlertCircle size={20} stroke={2} />} />
															</div>
														)}

														<div className="col-span-12 w-full">
															<FormField
																control={form.control}
																name="format_type"
																render={({ field }) => (
																	<FormItem>
																		<div className="flex flex-col w-full">
																			<FormLabel required>Select Format Type</FormLabel>
																		</div>
																		<FormGroup>
																			<Select
																				onValueChange={async (value) => {
																					field.onChange(value);

																					if (value) {
																						setTypes(value);
																					}
																				}}
																				defaultValue={field.value}
																				options={[
																					{
																						value: "upload_files",
																						label: "Upload Files",
																					},
																				]}
																			/>
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</div>
														{formatType === "text_editor" && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="type"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Select Type</FormLabel>
																			</div>
																			<FormGroup>
																				<Select
																					onValueChange={async (value) => {
																						field.onChange(value);
																						setType(value);
																						setSelectType(true);
																						if (value === "Custom") {
																							const response = await documentTokenMutation.mutateAsync({
																								file_type: "docx",
																								title: "New Document",
																								url: "https://sc-admin.revlv.com/blank.docx",
																								mode: "edit",
																							});
																							form.setValue("document_key", response.key);
																							setDocOpts({
																								token: response.token,
																								options: response.options,
																							});
																						}
																					}}
																					defaultValue={field.value}
																					options={[
																						{
																							value: "Custom",
																							label: "Custom",
																						},
																						{
																							value: "Template",
																							label: "Template",
																						},
																					]}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														{formatType === "text_editor" && type === "Template" && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="form_template_id"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel>Court Actions</FormLabel>
																			</div>
																			<FormGroup>
																				<ComboboxCreatable
																					defaultValue={field.form_template_id}
																					onValueChange={async (value) => {
																						const response = await documentTokenMutation.mutateAsync({
																							file_type: "docx",
																							title: value.medias.name,
																							url: value.medias.original_url,
																							mode: "edit",
																						});

																						form.setValue("form_template_id", value.value);

																						// form.setValue("activity", value.label);
																						form.setValue("document_key", response.key);
																						setDocOpts({
																							token: response.token,
																							options: response.options,
																						});
																					}}
																					loadOptions={async (value: string) => {
																						const items = await formTemplateService.list({
																							"filter[search]": value || data.activity,
																						});

																						return items.data.map((item) => ({
																							label: item.title,
																							value: item.id,
																							...item,
																						}));
																					}}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														{/* <div className="col-span-12 w-full">
                            <FormField
                              control={form.control}
                              name="case_document"
                              render={({ field }) => (
                                <FormItem>
                                  <div className="flex flex-col w-full">
                                    <FormLabel>Document</FormLabel>
                                  </div>
                                  <FormGroup>
                                    <InputFile
                                      ref={field.ref} // Use only ref instead of spreading field
                                      accept="application/pdf"
                                      onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        if (e.target?.files?.[0]) {
                                          const file = e.target.files[0];
                                          console.log(file);
                                          console.log(file.type);
                                          form.setValue("case_document", file, { shouldValidate: true });
                                          e.target.value = "";
                                          const fixedFile = new Blob([file], { type: "application/pdf" });
                                          const objectUrl = URL.createObjectURL(fixedFile);
                                          setDocument(objectUrl);
                                        }
                                      }}
                                    />
                                  </FormGroup>
                                </FormItem>
                              )}
                            />
                          </div> */}

														<div className="col-span-12 w-full">
															<FormField
																control={form.control}
																name="activity"
																render={({ field }) => (
																	<FormItem>
																		<div className="flex flex-col w-full">
																			<FormLabel required>Title</FormLabel>
																			<FormDescription />
																		</div>
																		<FormGroup>
																			<Input autoComplete="off" placeholder="Title" {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</div>

														{formatType === "upload_files" && (
															<div className="col-span-12 flex w-full">
																<FormField
																	control={form.control}
																	name="attachment"
																	render={() => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel>Upload Files</FormLabel>
																			</div>
																			<FormGroup>
																				<div className="flex flex-col w-full rounded-md border border-border">
																					<div className="relative flex flex-row items-center w-full h-[38px] max-h-[38px] p-1.5">
																						<input
																							type="file"
																							accept=".pdf,.doc,.docx"
																							ref={inputRef}
																							className="absolute inset-0 w-full h-full cursor-pointer opacity-0"
																							multiple
																							onChange={handleFileChange}
																						/>
																						<div className="flex flex-row items-center w-full gap-2">
																							<div className="inline-flex flex-row items-center px-3 h-[28px] gap-1 rounded bg-primary-600 font-semibold text-primary-foreground cursor-pointer">
																								<span className="text-sm">{multipleFiles.length > 0 ? "Add" : "Browse"}</span>
																							</div>
																							<span className="text-base text-foreground">
																								{multipleFiles.length === 1
																									? multipleFiles[0].name
																									: multipleFiles.length > 1
																										? "More than 2 files selected"
																										: "No file selected"}
																							</span>
																						</div>
																					</div>

																					{showAttachment && multipleFiles.length > 0 && (
																						<div className="flex flex-col w-full p-1.5 gap-2 border-t border-border">
																							{multipleFiles.map((file, index) => (
																								<div key={index} className="flex flex-row justify-between items-center">
																									<div className="items-center flex flex-row gap-2">
																										<div className="inline-flex shrink-0 justify-center items-center w-[32px] h-[32px] rounded-md bg-slate-200 text-foreground overflow-hidden">
																											<IconFile size={18} stroke={2} />
																										</div>
																										<div className="inline-flex flex-col">
																											<span className="truncate max-w-[240px] text-sm font-semibold text-foreground-dark">{file.name}</span>
																											<span className="text-sm text-foreground/50">{(file.size / 1024).toFixed(2)} KB</span>
																										</div>
																									</div>

																									<div className="inlineflex flex-row w-auto">
																										<Tooltip className="!z-[999]" content="Preview">
																											<button
																												className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md text-foreground/50 hover:text-foreground"
																												type="button"
																												color="red"
																												onClick={async () => {
																													const objectUrl = URL.createObjectURL(file);
																													if (file && file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
																														const responseDocs = await convertDocsMutation.mutateAsync(file);

																														const response = await documentTokenMutation.mutateAsync({
																															file_type: "docx",
																															title: responseDocs?.path,
																															url: `${baseUrl}${responseDocs?.url}`,
																															mode: "view",
																														});

																														setDocOpts({
																															token: response.token,
																															options: response.options,
																														});

																														setDocument(responseDocs);
																													} else {
																														setDocument(objectUrl);
																													}
																												}}
																											>
																												<IconEye size={18} stroke={2} />
																											</button>
																										</Tooltip>
																										<Tooltip className="!z-[999]" content="Remove">
																											<button
																												className="inline-flex justify-center items-center w-[28px] h-[28px] ml-auto rounded-md text-danger-600 hover:text-danger-500"
																												type="button"
																												color="red"
																												onClick={() => handleRemoveAttachment(index)}
																											>
																												<IconTrash size={18} stroke={2} />
																											</button>
																										</Tooltip>
																									</div>
																								</div>
																							))}
																						</div>
																					)}
																				</div>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{!isSummon && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="assigned_to"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Recipient</FormLabel>
																			</div>
																			<FormGroup>
																				<Select
																					onValueChange={async (value) => {
																						field.onChange(value);
																					}}
																					defaultValue={field.value}
																					options={[
																						{
																							value: "Both",
																							label: "Both",
																						},
																						{
																							value: "Plaintiff",
																							label: "Plaintiff",
																						},
																						{
																							value: "Defendant",
																							label: "Defendant",
																						},
																					]}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{title !== "POST_JUDGMENT" && title !== "JUDGMENT" && (
															<div className="col-span-12 flex flex-col w-full">
																<FormField
																	control={form.control}
																	name="with_fees"
																	render={() => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel>Options</FormLabel>
																			</div>
																			<CheckboxGroup>
																				<FormItem>
																					<FormGroup>
																						<div className="flex flex-row w-full gap-2">
																							<FormControl>
																								<Checkbox
																									size="lg"
																									name="with_compliance"
																									checked={compliance}
																									onCheckedChange={(value) => {
																										setCompliance(value);
																										setDeadline(value);
																										setInCalendar(value);
																										form.setValue("with_compliance", value);
																										form.setValue("with_deadline", value);
																										// form.setValue("in_calendar", value);
																									}}
																								/>
																							</FormControl>
																							<FormLabel>
																								<div className="flex flex-col w-full">
																									<span className="text-base text-foreground">With Compliance</span>
																									<span className="text-sm text-muted-foreground">This activity will have compliance.</span>
																								</div>
																							</FormLabel>
																						</div>
																					</FormGroup>
																					<FormMessage />
																				</FormItem>
																				{!isSummon && (
																					<>
																						<FormItem>
																							<FormGroup>
																								<div className="flex flex-row w-full gap-2">
																									<FormControl>
																										<Checkbox
																											size="lg"
																											name="with_deadline"
																											checked={deadline}
																											disabled={!compliance}
																											onCheckedChange={(value) => {
																												setDeadline(value);
																												form.setValue("with_deadline", value);
																												setInCalendar(value);
																												form.setValue("in_calendar", value);
																											}}
																										/>
																									</FormControl>
																									<FormLabel>
																										<div className="flex flex-col w-full">
																											<span className="text-base text-foreground">With Deadline</span>
																											<span className="text-sm text-muted-foreground">This activity will have deadline.</span>
																										</div>
																									</FormLabel>
																								</div>
																							</FormGroup>
																							<FormMessage />
																						</FormItem>

																						<FormItem>
																							<FormGroup>
																								<div className="flex flex-row w-full gap-2">
																									<FormControl>
																										<Checkbox
																											size="lg"
																											name="in_calendar"
																											checked={inCalendar}
																											onCheckedChange={(value) => {
																												setInCalendar(value);
																												form.setValue("in_calendar", value);
																											}}
																										/>
																									</FormControl>
																									<FormLabel>
																										<div className="flex flex-col w-full">
																											<span className="text-base text-foreground">In Calendar</span>
																											<span className="text-sm text-muted-foreground">This activity will be shown in calendar.</span>
																										</div>
																									</FormLabel>
																								</div>
																							</FormGroup>
																							<FormMessage />
																						</FormItem>

																						{!decision &&
																							nextStage?.is_visible &&
																							allowPostAs &&
																							data.stage === caseStages.PRELIMINARY_PROCEEDINGS &&
																							partyTypeReference === "Bcc" && (
																								<FormItem>
																									<FormGroup>
																										<div className="flex flex-row w-full gap-2">
																											<FormControl>
																												<Checkbox
																													size="lg"
																													name="sheriff_report"
																													checked={sheriffReport}
																													onCheckedChange={(value) => {
																														form.setValue("sheriff_report", value);
																													}}
																												/>
																											</FormControl>
																											<FormLabel>
																												<div className="flex flex-col w-full">
																													<span className="text-base text-foreground">Sheriff's Report</span>
																													<span className="text-sm text-muted-foreground">This will add sherrif report.</span>
																												</div>
																											</FormLabel>
																										</div>
																									</FormGroup>
																									<FormMessage />
																								</FormItem>
																							)}
																					</>
																				)}
																			</CheckboxGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{compliance && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="respondents"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>To be Complied By</FormLabel>
																			</div>
																			<FormGroup>
																				<TagsInput
																					isMulti={true}
																					defaultValue={field.value}
																					onValueChange={(value) => {
																						field.onChange(value);
																					}}
																					loadOptions={async () =>
																						caseRecord.collaborators
																							.filter((item) => item.role === caseUserRoles.DEFENDANT || item.role === caseUserRoles.PLAINTIFF)
																							.map((item) => ({
																								label: `${item?.full_name} - ${item.role}`,
																								value: item.id,
																							}))
																					}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														{inCalendar && !isSummon && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="scheduled_at"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Calendar Date</FormLabel>
																			</div>
																			<FormGroup>
																				{/* <FormCalendar showTimePicker={true} showIcon={true} currentDate={null} {...field} /> */}
																				<Input autoComplete="off" type="date" placeholder="Calendar Date" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														{!moveTo && !isSummon && allowPostAs && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="party_type"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Post As</FormLabel>
																			</div>
																			<FormGroup>
																				<Combobox
																					isMulti={false}
																					defaultValue={field.value}
																					placeholderValue={{
																						label: selectedCollaborator[0] ? selectedCollaborator[0].label : field.value,
																					}}
																					onValueChange={(value) => {
																						if (value) {
																							field.onChange(value.value);
																							setPartyTypeReference(value.user_id ? value.user_id : value.value);
																							const postingsFor = collaborators
																								.filter(
																									(item) =>
																										item.user_id === value.user_id || item.counsels?.some((counsel) => counsel.user_id === value.user_id),
																								)
																								.map((item) => ({
																									label: `${item?.full_name} - ${item.role}`,
																									value: item.id,
																								}));
																							if (postingsFor.length === 1) {
																								setDefaultDefendants(postingsFor);
																							}
																							if (caseRecord.case_classification === "Small Claims") {
																								setDefaultDefendants([value]);
																							}
																						}
																					}}
																					returnFullOption={true}
																					loadOptions={async () => {
																						const baseOption = [
																							{
																								value: "Bcc",
																								label: "BCC",
																								reference_id: "Bcc",
																							},
																							{
																								value: "Judge",
																								label: "Judge",
																								reference_id: "Judge",
																							},
																						];
																						const filteredCollaborators =
																							caseRecord.case_classification === "Small Claims"
																								? collaborators.filter((item) => item.role === "Plaintiff" || item.role === "Defendant")
																								: collaborators.filter((item) => item.role === "Counsel (P)" || item.role === "Counsel (D)");

																						const seenNames = new Set();
																						const collaboratorOptions = filteredCollaborators
																							.filter((item) => {
																								if (seenNames.has(item.full_name)) {
																									return false;
																								}
																								seenNames.add(item.full_name);
																								return true;
																							})
																							.map((item) => ({
																								label: ` ${caseRecord.case_classification !== "Small Claims" ? "Atty." : ""} ${item?.full_name} - ${getRole(item.role)}`,
																								value: item.id,
																								user_id: item.user_id,
																								role: item.role,
																								reference_id: item.reference_id,
																							}));

																						return [...baseOption, ...collaboratorOptions];
																					}}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{!moveTo && !isSummon && allowPostAs && partyTypeReference !== "Bcc" && caseRecord.case_classification !== "Small Claims" && (
															<div className="col-span-12 w-full">
																<FormField
																	key={partyTypeReference}
																	control={form.control}
																	name="defendants"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Posting For</FormLabel>
																			</div>
																			<FormGroup>
																				<FormControl>
																					<TagsInput
																						isMulti={true}
																						defaultValue={defaultDefendants}
																						onValueChange={(value) => {
																							field.onChange(value);
																						}}
																						loadOptions={async () =>
																							collaborators
																								.filter((item) => item.counsels?.some((counsel) => counsel.user_id === partyTypeReference))
																								.map((item) => ({
																									label: `${item?.full_name} - ${item.role}`,
																									value: item.id,
																								}))
																						}
																					/>
																				</FormControl>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{!moveTo && !isSummon && allowPostAs && partyTypeReference !== "Bcc" && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="action_type"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Select Action</FormLabel>
																			</div>
																			<FormGroup>
																				<Select
																					onValueChange={async (value) => {
																						field.onChange(value);
																						if (value === "with_grant") {
																							form.setValue("with_grant", true);
																							form.setValue("with_compliance", false);
																						}
																						if (value === "with_compliance") {
																							form.setValue("with_compliance", true);
																							form.setValue("with_grant", false);
																						}
																					}}
																					defaultValue={field.value}
																					options={[
																						{
																							value: "with_grant",
																							label: "Motions",
																						},
																						{
																							value: "with_compliance",
																							label: "Pleadings/Other Submissions",
																						},
																					]}
																				/>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														{/* {!moveTo && !isSummon && allowPostAs && partyTypeReference !== "Bcc" && (
														<div className="col-span-12 w-full">
															<FormField
																control={form.control}
																name="submitted_at"
																render={({ field }) => (
																	<FormItem>
																		<div className="flex flex-col w-full">
																			<FormLabel>Date Submitted</FormLabel>
																		</div>
																		<FormGroup>
																			<Input autoComplete="off" type="datetime-local" placeholder="Date Submitted" {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</div>
													)} */}

														{deadline && !isSummon && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="due_at"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Deadline</FormLabel>
																			</div>
																			<FormGroup>
																				{/* <FormCalendar showTimePicker={true} showIcon={true} currentDate={null} {...field} /> */}
																				<Input autoComplete="off" type="date" placeholder="Deadline" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														{partyTypeReference === "Bcc" ? (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="ordered_at"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Date Ordered</FormLabel>
																			</div>
																			<FormGroup>
																				<Input autoComplete="off" type="datetime-local" placeholder="Date Ordered" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														) : (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="filed_at"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Date Filed</FormLabel>
																			</div>
																			<FormGroup>
																				<Input autoComplete="off" type="datetime-local" placeholder="Date Filed" {...field} />
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}

														<div className="col-span-12 w-full ">
															<FormField
																control={form.control}
																name="remarks"
																render={({ field }) => (
																	<FormItem>
																		<div className="flex flex-col w-full">
																			<FormLabel>Remarks</FormLabel>
																			<FormDescription />
																		</div>
																		<FormGroup>
																			<Textarea {...field} />
																		</FormGroup>
																		<FormMessage />
																	</FormItem>
																)}
															/>
														</div>

														{((!decision && !moveTo && nextStage?.is_visible && !isSummon) ||
															(nextStage?.is_button && !moveTo) ||
															(nextStage?.is_judgment && !moveTo)) && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="move_to_next_stage"
																	render={({ field }) => (
																		<FormItem>
																			<FormGroup>
																				<div className="flex flex-row w-full gap-2">
																					<FormControl>
																						<Checkbox size="lg" checked={isNextStage} onCheckedChange={field.onChange} />
																					</FormControl>
																					<FormLabel>
																						<div className="flex flex-col w-full">
																							<span className="text-base text-foreground">
																								Move to {nextStage?.is_judgment ? "Post Judgment" : nextStage?.label}
																							</span>
																						</div>
																					</FormLabel>
																				</div>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														{!decision && nextStage?.is_judgment && isNextStage && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="with_decision"
																	render={({ field }) => (
																		<FormItem>
																			<FormGroup>
																				<div className="flex flex-row w-full gap-2">
																					<FormControl>
																						<Checkbox size="lg" checked={withDecision} onCheckedChange={field.onChange} />
																					</FormControl>
																					<FormLabel>
																						<div className="flex flex-col w-full">
																							<span className="text-base text-foreground">With Decision</span>
																						</div>
																					</FormLabel>
																				</div>
																			</FormGroup>
																			<FormMessage />
																		</FormItem>
																	)}
																/>
															</div>
														)}
														{withDecision && caseRecord.case_classification !== "Small Claims" && (
															<div className="col-span-12 w-full">
																<FormField
																	control={form.control}
																	name="decision_at"
																	render={({ field }) => (
																		<FormItem>
																			<div className="flex flex-col w-full">
																				<FormLabel required>Appeal Deadline</FormLabel>
																			</div>
																			<div className="col-span-12 w-full">
																				<Button
																					type="button"
																					variant={"ghost"}
																					onClick={() => {
																						updateDate(15);
																					}}
																				>
																					15D
																				</Button>
																				<Button
																					type="button"
																					className="ml-2"
																					variant={"ghost"}
																					onClick={() => {
																						updateDate(30);
																					}}
																				>
																					30D
																				</Button>
																			</div>
																			<FormItem>
																				<div className="flex flex-col w-full">
																					<FormLabel required>Appeal Deadline</FormLabel>
																				</div>
																				<FormGroup>
																					<Input autoComplete="off" type="date" placeholder="Appeal Deadline" {...field} />
																				</FormGroup>
																				<FormMessage />
																			</FormItem>
																		</FormItem>
																	)}
																/>
															</div>
														)}

														<div className="col-span-12 flex flex-col w-full my-1 gap-3">
															{isNextStage &&
																!moveTo &&
																nextStage?.is_button !== true &&
																data.stage !== caseStages.POST_JUDGMENT &&
																data.stage !== caseStages.PRE_TRIAL_CONFERENCE &&
																data.stage !== caseStages.JUDGMENT && (
																	<Alert
																		type="info"
																		allowClose={false}
																		title={`After submitting, the case will move to ${nextStage?.is_judgment ? "Post Judgment" : nextStage?.label} status.`}
																		icon={<IconAlertCircle size={20} stroke={2} />}
																	/>
																)}
															{decision && (
																<Alert
																	type="info"
																	allowClose={false}
																	title="After submitting, the case will move to Post Judgment status."
																	icon={<IconAlertCircle size={20} stroke={2} />}
																/>
															)}

															{/* {nextStage?.can_close && isNextStage === true && (
                              <div className="col-span-12 w-full">
                                {nextStage?.is_visible && (
                                  <FormField
                                    control={form.control}
                                    name="close_case"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormGroup>
                                          <div className="flex flex-row w-full gap-2">
                                            <FormControl>
                                              <Checkbox size="lg" disabled={!isNextStage} onCheckedChange={field.onChange} />
                                            </FormControl>
                                            <FormLabel>
                                              <div className="flex flex-col w-full">
                                                <span className="text-base text-foreground">Close Case?</span>
                                              </div>
                                            </FormLabel>
                                          </div>
                                        </FormGroup>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                )}
                              </div>
                            )} */}
														</div>
													</div>
												</fieldset>
											</ScrollArea>
										</div>
									</Panel>

									<PanelResizeHandle className="group flex justify-center items-center w-[12px] border-r border-l border-border/50 bg-white">
										<div className="inline-flex w-[4px] h-[40px] rounded-sm bg-foreground/50 group-hover:bg-foreground/90" />
									</PanelResizeHandle>

									<Panel defaultSize={70} minSize={70} maxSize={70} className="flex flex-col shrink grow w-full h-full bg-slate-100">
										{formatType === "text_editor" && <OnlyOfficeEditor token={docOpts.token} options={docOpts.options} />}

										{formatType === "upload_files" && document?.url && <OnlyOfficeEditor token={docOpts.token} options={docOpts.options} />}

										{formatType === "upload_files" && !document?.url && <PdfViewer file={document} documents={document} />}

										{!formatType && (
											<Blanket className="my-auto self-center" width="sm">
												<BlanketImage icon={<IconTemplate size={24} stroke={1.5} />} />
												<BlanketInfo title="No Selected Template" message="Select Template on Select Type to load the default template for this form." />
											</Blanket>
										)}
									</Panel>
								</PanelGroup>
								<div className="flex shrink-0 flex-row items-center justify-end w-full h-[65px] px-4 gap-3 border-t border-slate-200 bg-background">
									<Button
										variant="ghost"
										type="button"
										onClick={() => {
											closeModal();
										}}
									>
										Cancel
									</Button>
									{data.state === "draft" && (
										<Button
											type="button"
											onClick={() => {
												handleDraft(form.getValues());
											}}
											variant={"green"}
											disabled={isSubmitting}
											loading={isSubmitting}
										>
											Save As Draft
										</Button>
									)}
									<Button disabled={isSubmitting} loading={isSubmitting}>
										Submit
									</Button>
								</div>
							</TabsContent>
							<TabsContent className="h-full" value="hearing">
								<div className="flex flex-col h-full justify-between">
									<ScrollArea className="h-[calc(100svh-170px)]">
										{isHoliday && (
											<div className="col-span-12 w-full py-1">
												<Alert type="info" allowClose={false} title="The date selected is a Holiday" icon={<IconAlertCircle size={20} stroke={2} />}>
													The selected date falls on a holiday. Please take this into consideration when scheduling this hearing. You can still schedule a
													hearing on this date.
												</Alert>
											</div>
										)}
										<div className="flex flex-col gap-4 p-2">
											{hearings.fields.map((hearing, index) => (
												<div className="border rounded-md w-full p-4" key={index}>
													<div className="flex flex-col">
														<div className="flex flex-row">
															<div className="flex flex-col w-full gap-3">
																<div className="flex flex-row items-center justify-between">
																	<div className="flex flex-col w-full">
																		<span className="text-lg font-semibold text-foreground-dark">
																			Hearing # {index + 1} - {formatDate(form.watch(`hearings[${index}].due_date`))}{" "}
																		</span>
																	</div>
																	<button
																		className="inline-flex justify-center items-center px-3 text-danger-600 hover:text-danger-500"
																		type="button"
																		onClick={() => hearings.remove(index)}
																	>
																		<IconTrash size={18} stroke={2} />
																	</button>
																</div>
																<div className="grid grid-cols-12 w-full gap-x-4 gap-y-3">
																	{!form.watch(`hearings[${index}].id`) && (
																		<div className="col-span-2 w-full">
																			<FormField
																				control={form.control}
																				name={`hearings[${index}].due_date`}
																				render={({ field }) => (
																					<FormItem>
																						<div className="flex flex-col w-full">
																							<FormLabel required>Date and Time</FormLabel>
																							<FormDescription />
																						</div>
																						<FormGroup>
																							<Input
																								type="datetime-local"
																								onChange={async (e) => {
																									const value = e.target.value;
																									const response = await eventService.validateHearing(value, caseRecord.id);
																									form.setValue(`hearings.${index}.hearing_count`, response);
																									field.onChange(value);
																								}}
																							/>
																						</FormGroup>
																						<FormMessage />
																					</FormItem>
																				)}
																			/>
																		</div>
																	)}
																	<div className="col-span-3 w-full">
																		<FormField
																			control={form.control}
																			name={`hearings[${index}].remarks`}
																			render={({ field }) => (
																				<FormItem>
																					<div className="flex flex-col w-full">
																						<FormLabel required>Activity/Event</FormLabel>
																						<FormDescription />
																					</div>
																					<FormGroup>
																						<Input autoComplete="off" placeholder="e.g Presentation of Evidence" {...field} />
																					</FormGroup>
																					<FormMessage />
																				</FormItem>
																			)}
																		/>
																	</div>
																	<div className="col-span-3 w-full">
																		<FormField
																			control={form.control}
																			name={`hearings[${index}].stage`}
																			render={({ field }) => (
																				<FormItem>
																					<div className="flex flex-col w-full">
																						<FormLabel required>Stage</FormLabel>
																						<FormDescription />
																					</div>
																					<FormGroup>
																						<Select
																							onValueChange={async (value) => {
																								field.onChange(value);
																							}}
																							defaultValue={field.value}
																							options={caseStagesNew.data
																								.filter((c) => c.is_visible)
																								.map((c) => ({
																									label: c.label,
																									value: c.name,
																								}))}
																						/>
																					</FormGroup>
																				</FormItem>
																			)}
																		/>
																	</div>

																	<div className="col-span-2 w-full">
																		<FormField
																			control={form.control}
																			name={`hearings[${index}].hearing_mode`}
																			render={({ field }) => (
																				<FormItem>
																					<div className="flex flex-col w-full">
																						<FormLabel required>Hearing Mode</FormLabel>
																						<FormDescription />
																					</div>
																					<FormGroup>
																						<Select
																							onValueChange={async (value) => {
																								field.onChange(value);
																							}}
																							defaultValue={field.value}
																							options={[
																								{
																									value: "Face to Face",
																									label: "Face to Face",
																								},
																								{
																									value: "Online",
																									label: "Online",
																								},
																							]}
																						/>
																					</FormGroup>
																					<FormMessage />
																				</FormItem>
																			)}
																		/>
																	</div>
																	<div className="col-span-2 w-full">
																		<FormField
																			control={form.control}
																			name={`hearings.${index}.hearing_count`}
																			render={({ field }) => {
																				return (
																					<FormItem>
																						<div className="flex flex-col w-full">
																							<FormLabel>Settings</FormLabel>
																							<FormDescription />
																						</div>
																						<FormGroup>
																							<Tooltip className="z-[999]" content="Click to open calendar in new tab" side="top">
																								{/* <button
                                      onClick={() => {
                                        window.open(`/calendar?view=true&date=${form.getValues(`hearings.${index}.due_date`)}`);
                                      }}
                                      type="button"
                                      className="text-start border w-full rounded-md block bg-muted text-muted-foreground px-3"
                                    > */}
																								<Button
																									className="w-full !justify-start !containerbg-muted text-muted-foreground"
																									variant="ghost"
																									onClick={() => {
																										window.open(`/calendar?view=true&date=${form.getValues(`hearings.${index}.due_date`)}`);
																									}}
																								>
																									{field.value || 0}
																								</Button>
																								{/* </button> */}
																							</Tooltip>
																						</FormGroup>
																					</FormItem>
																				);
																			}}
																		/>
																	</div>

																	<div className="col-span-4 w-full">
																		<FormField
																			control={form.control}
																			name={`hearings[${index}].witness_name`}
																			render={({ field }) => (
																				<FormItem>
																					<div className="flex flex-col w-full">
																						<FormLabel>Witness /Judicial Affidavits</FormLabel>
																						<FormDescription />
																					</div>
																					<FormGroup>
																						<FormField
																							control={form.control}
																							name={`hearings[${index}].witness_name`}
																							render={({ field }) => (
																								<TagsInput
																									isMulti={true}
																									defaultValue={field.value}
																									onValueChange={(value) => {
																										field.onChange(value);
																										if (value.length > 0) {
																											setParties(value[0].parties);
																										}
																									}}
																									loadOptions={async (value: string) => {
																										const items = await caseEvidenceService.listAllByCaseId(caseRecord.id, {
																											"filter[evidence_type]": "Testimonial",
																											"filter[case_id]": caseRecord.id,
																											"filter[search]": value,
																										});
																										return items.data.map((item) => ({
																											label: item.witness_name,
																											value: item.id,
																											parties: item.parties,
																											case_record_user_id: item.case_record_user_id || null,
																										}));
																									}}
																								/>
																							)}
																						/>
																					</FormGroup>
																				</FormItem>
																			)}
																		/>
																	</div>

																	<div className="col-span-8 w-full">
																		<FormField
																			control={form.control}
																			name={`hearings[${index}].exhibits`}
																			render={() => (
																				<FormItem>
																					<div className="flex flex-col w-full">
																						<FormLabel>Exhibits</FormLabel>
																						<FormDescription />
																					</div>
																					<FormGroup>
																						<FormField
																							control={form.control}
																							name={`hearings[${index}].exhibits`}
																							render={({ field }) => (
																								<TagsInput
																									key={parties}
																									isMulti={true}
																									defaultValue={field.value}
																									onValueChange={(value) => {
																										field.onChange(value);
																									}}
																									loadOptions={async (value: string) => {
																										const items = await caseEvidenceService.listAllByCaseId(caseRecord.id, {
																											"filter[case_id]": caseRecord.id,
																											"filter[parties]": parties,
																											"filter[search]": value,
																										});
																										return items.data
																											.filter((i) => i.evidence_type !== "Testimonial" && !i.parent_id)
																											.map((item) => ({
																												label: `${item.exhibit} - ${item.description}`,
																												value: item.id,
																												parties: item.parties,
																												witness_name: item.witness_name || null,
																												case_record_user_id: item.case_record_user_id || null,
																											}));
																									}}
																								/>
																							)}
																						/>
																					</FormGroup>
																				</FormItem>
																			)}
																		/>
																	</div>
																</div>
															</div>
														</div>
													</div>
												</div>
											))}
											<Button type="button" onClick={handleAddRow} size="default" variant="ghost" className="w-full">
												<IconCirclePlus size={18} stroke={2} /> Add New Schedule
											</Button>
										</div>
									</ScrollArea>

									<div className="flex shrink-0 flex-row items-center justify-end w-full h-[65px] px-4 gap-3 border-t border-slate-200 bg-background">
										<Button
											variant="ghost"
											type="button"
											onClick={() => {
												closeModal();
											}}
										>
											Cancel
										</Button>
										{data.state === "draft" && (
											<Button
												type="button"
												onClick={() => {
													handleDraft(form.getValues());
												}}
												variant={"green"}
												disabled={isSubmitting}
												loading={isSubmitting}
											>
												Save As Draft
											</Button>
										)}
										<Button disabled={isSubmitting} loading={isSubmitting}>
											Submit
										</Button>
									</div>
								</div>
							</TabsContent>
						</Tabs>
					</div>
				</div>
			</form>
		</Form>
	);
}
